import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Button, Spinner, Badge } from "react-bootstrap";
import { Alert } from "@mui/material";
import DataTable from "../DataTable";
import { getOwnerTransactionHistory } from "../utils";
import { 
  FaArrowLeft, 
  FaMoneyBillWave, 
  FaWallet, 
  FaArrowDown, 
  FaArrowUp,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMoneyCheckAlt
} from "react-icons/fa";

export default function OwnerPaymentDetails() {
  const { dynamicId: ownerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  console.log('OwnerPaymentDetails Component Mounted/Rendered');
  console.log('Owner ID from params:', ownerId);

  useEffect(() => {
    console.log('useEffect triggered with ownerId:', ownerId);
    if (ownerId) {
      fetchOwnerDetails();
    } else {
      console.warn('No ownerId found in params!');
    }
  }, [ownerId]);

  const fetchOwnerDetails = async () => {
    try {
      setLoading(true);
      setError("");
      console.log('=== FETCHING OWNER DETAILS ===');
      console.log('Owner ID:', ownerId);
      const result = await getOwnerTransactionHistory(ownerId);
      console.log('=== RESULT FROM getOwnerTransactionHistory ===');
      console.log('Full result:', result);
      console.log('Current Balance:', result?.currentBalance);
      console.log('Total Earnings:', result?.totalEarnings);
      console.log('Total Withdrawals:', result?.totalWithdrawals);
      console.log('Transactions count:', result?.transactions?.length);
      setData(result);
    } catch (err) {
      console.error("Error fetching owner details:", err);
      setError("Failed to load owner payment details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amountInCents) => {
    const amount = (amountInCents || 0) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status, type) => {
    if (type === 'payment') {
      switch (status?.toLowerCase()) {
        case 'completed':
          return <Badge bg="success">Completed</Badge>;
        case 'pending':
          return <Badge bg="warning" text="dark">Pending</Badge>;
        case 'failed':
          return <Badge bg="danger">Failed</Badge>;
        // DB stores the legacy value "refunded" when the security-deposit hold
        // is voided at trip end (no damages claimed). Nothing is actually
        // refunded — the authorization is simply released. Display a clearer
        // label without touching stored values.
        case 'refunded':
          return <Badge bg="success">Hold Released</Badge>;
        // DB stores "Dispute Resolved" when a dispute was filed and then
        // closed by capturing all/part of the security-deposit hold. Show as
        // "Damages Charged" so the meaning is obvious.
        case 'dispute resolved':
          return <Badge bg="info">Damages Charged</Badge>;
        case 'disputed':
          return <Badge bg="warning" text="dark">Disputed</Badge>;
        default:
          return <Badge bg="secondary">{status || 'Unknown'}</Badge>;
      }
    } else {
      switch (status) {
        case 'Processed':
          return <Badge bg="success">Processed</Badge>;
        case 'Approved':
          return <Badge bg="info">Approved</Badge>;
        case 'Pending':
          return <Badge bg="warning" text="dark">Pending</Badge>;
        case 'Disapproved':
          return <Badge bg="danger">Disapproved</Badge>;
        default:
          return <Badge bg="secondary">{status || 'Unknown'}</Badge>;
      }
    }
  };

  const columns = [
    {
      Header: "Type",
      accessor: "type",
      Cell: ({ row, value }) => (
        <div className="d-flex align-items-center">
          {row.original.reason === 'Tip' ? (
            <>
              <FaMoneyBillWave className="text-success me-2" />
              <span className="text-success">Tip</span>
            </>
          ) : value === 'payment' ? (
            <>
              <FaArrowUp className="text-success me-2" />
              <span className="text-success">Payment</span>
            </>
          ) : (
            <>
              <FaArrowDown className="text-danger me-2" />
              <span className="text-danger">Withdrawal</span>
            </>
          )}
        </div>
      )
    },
    {
      Header: "Date",
      accessor: "date",
      Cell: ({ value }) => formatDate(value)
    },
    {
      Header: "Amount",
      accessor: "amount",
      Cell: ({ row }) => (
        <span className={row.original.type === 'payment' ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
          {row.original.type === 'payment' ? '+' : '-'}{formatCurrency(row.original.amount)}
        </span>
      )
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) => getStatusBadge(row.original.status, row.original.type)
    },
    {
      Header: "Running Balance",
      accessor: "runningBalance",
      Cell: ({ value }) => (
        <span className="fw-bold">{formatCurrency(value)}</span>
      )
    },
    {
      Header: "Details",
      accessor: "bookingId",
      Cell: ({ row }) => {
        if (row.original.type === 'payment') {
          return (
            <div>
              <small className="text-muted">
                {row.original.reason || 'Booking'}
                {row.original.bookingId && (
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="p-0 ms-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/event/${row.original.bookingId}`);
                    }}
                  >
                    View Booking
                  </Button>
                )}
              </small>
            </div>
          );
        }
        return (
            <div>
              <small className="text-muted">
                Withdrawal Request
                {row.original.bookingId && (
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="p-0 ms-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/event/${row.original.bookingId}`);
                    }}
                  >
                    View Booking
                  </Button>
                )}
              </small>
            </div>
          );
      }
    }
  ];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading owner details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Button 
          variant="outline-secondary" 
          className="mb-4"
          onClick={() => navigate('/dashboard/payments-dashboard')}
        >
          <FaArrowLeft className="me-2" /> Back to Dashboard
        </Button>
        <Alert severity="error">{error}</Alert>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Button 
          variant="outline-secondary" 
          className="mb-4"
          onClick={() => navigate('/dashboard/payments-dashboard')}
        >
          <FaArrowLeft className="me-2" /> Back to Dashboard
        </Button>
        <Alert severity="warning">No data found for this owner.</Alert>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="row mb-4">
        <div className="col">
          <Button 
            variant="outline-secondary" 
            className="mb-3"
            onClick={() => navigate('/dashboard/payments-dashboard')}
          >
            <FaArrowLeft className="me-2" /> Back to Dashboard
          </Button>
          <div className="page-description">
            <h1>Payment Details</h1>
            <p className="text-muted">Transaction history for {data.owner?.name || 'Boat Owner'}</p>
          </div>
        </div>
      </div>

      {/* Owner Info Card */}
      <Row className="mb-4">
        <Col xs={12} lg={3} className="mb-3 mb-lg-0">
          <div className="card h-100">
            <div className="card-body p-3">
              <h6 className="card-title mb-3">
                <FaUser className="me-2 text-primary" />
                Owner Information
              </h6>
              <div className="mb-2">
                <strong style={{ fontSize: '0.9rem' }}>{data.owner?.name || 'Unknown'}</strong>
              </div>
              {data.owner?.email && (
                <div className="mb-1 text-muted" style={{ fontSize: '0.85rem' }}>
                  <FaEnvelope className="me-2" />
                  {data.owner.email}
                </div>
              )}
              {data.owner?.phone && (
                <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                  <FaPhone className="me-2" />
                  {data.owner.phone}
                </div>
              )}
              {data.owner?.taxInformation?.taxIdType && (
                <div className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>
                  Tax ID: {data.owner.taxInformation.taxIdType}
                  {data.owner.taxInformation.taxIdLast4
                    ? ` ending in ${data.owner.taxInformation.taxIdLast4}`
                    : ' on file'}
                </div>
              )}
            </div>
          </div>
        </Col>

        {/* Summary Cards */}
        <Col xs={12} sm={4} lg={3} className="mb-3 mb-sm-0">
          <div className="card h-100">
            <div className="card-body p-3">
              <div className="d-flex align-items-center">
                <div className="rounded-circle p-2 bg-success bg-opacity-10 me-2">
                  <FaMoneyBillWave className="text-success" size={20} />
                </div>
                <div>
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>Total Earnings</h6>
                  <h5 className="mb-0 text-success" style={{ fontSize: '1.1rem' }}>{formatCurrency(data.totalEarnings)}</h5>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={4} lg={3} className="mb-3 mb-sm-0">
          <div className="card h-100">
            <div className="card-body p-3">
              <div className="d-flex align-items-center">
                <div className="rounded-circle p-2 bg-danger bg-opacity-10 me-2">
                  <FaMoneyCheckAlt className="text-danger" size={20} />
                </div>
                <div>
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>Total Withdrawals</h6>
                  <h5 className="mb-0 text-danger" style={{ fontSize: '1.1rem' }}>{formatCurrency(data.totalWithdrawals || 0)}</h5>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={4} lg={3}>
          <div className="card h-100">
            <div className="card-body p-3">
              <div className="d-flex align-items-center">
                <div className="rounded-circle p-2 bg-primary bg-opacity-10 me-2">
                  <FaWallet className="text-primary" size={20} />
                </div>
                <div>
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>Current Balance</h6>
                  <h5 className="mb-0 text-primary" style={{ fontSize: '1.1rem' }}>
                    {formatCurrency(data.currentBalance)}
                  </h5>
                  {console.log('=== RENDERING CURRENT BALANCE ===')}
                  {console.log('data.=========:', data)}
                  {console.log('Formatted:', formatCurrency(data.currentBalance))}
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Transaction History */}
      <div className="row">
        <div className="col">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Transaction History</h5>
            </div>
            <div className="card-body">
              {data.transactions && data.transactions.length > 0 ? (
                <DataTable
                  columns={columns}
                  data={data.transactions}
                />
              ) : (
                <div className="text-center py-5">
                  <FaWallet size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">No transactions found</h5>
                  <p className="text-muted">This owner has no payment or withdrawal history yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
